import type { AisCoverage, Call, ObservedCall, PortSummary, ShipDetailResponse, ShipNow } from "@cruise/core";
import {
  actualTimes,
  callStart,
  callTimes,
  compact,
  degrees,
  knots,
  latLon,
  minutesAgo,
  portDate,
  stateChip,
  stateLabel,
  thousands,
  utcDayTime,
} from "../format";

/** Arrival or departure windows wider than this are shown as a range. */
const WINDOW_SHOWN_MS = 30 * 60_000;
const OBSERVED_CALLS_SHOWN = 5;

function observedArrival(call: ObservedCall): string {
  if (call.arrivedAfter && Date.parse(call.arrivedAt) - Date.parse(call.arrivedAfter) > WINDOW_SHOWN_MS) {
    return `arrived ${utcDayTime(call.arrivedAfter)}–${utcDayTime(call.arrivedAt)}`;
  }
  return `arrived ${utcDayTime(call.arrivedAt)}`;
}

function observedDeparture(call: ObservedCall, stillHeard: boolean): string {
  if (!call.departedBefore) {
    return stillHeard ? "still there" : `last heard there ${utcDayTime(call.lastStoppedAt)}`;
  }
  if (Date.parse(call.departedBefore) - Date.parse(call.lastStoppedAt) > WINDOW_SHOWN_MS) {
    return `left ${utcDayTime(call.lastStoppedAt)}–${utcDayTime(call.departedBefore)}`;
  }
  return `left ${utcDayTime(call.lastStoppedAt)}`;
}

const SCHEDULE_NOTE =
  "Schedules come from the ports' own published lists. Ports without a list we can read are not included yet.";

type Timezones = ReadonlyMap<string, string>;

function callPhase(call: Call, now: number): "done" | "now" | "" {
  if (call.actualDeparture) return "done";
  if (call.actualArrival) return "now";
  const start = Date.parse(callStart(call));
  const end = call.departure ? Date.parse(call.departure) : start + (call.dateOnly ? 86_400_000 : 0);
  if (end < now) return "done";
  if (start <= now) return "now";
  return "";
}

export function ShipCard({
  ship,
  detail,
  timezones,
  now,
}: {
  ship: ShipNow | null;
  detail: ShipDetailResponse | null;
  timezones: Timezones;
  now: number;
}) {
  if (!ship) {
    return (
      <div className="card glass">
        <div className="panel-h">
          <span className="label">Ship</span>
        </div>
        <div className="body note">Click a ship on the map to follow it.</div>
      </div>
    );
  }
  const pos = ship.position;
  const calls = (detail?.ship.imo === ship.imo ? detail.calls : []).slice(0, 6);
  const observedCalls = (detail?.ship.imo === ship.imo ? detail.portCalls : []).slice(0, OBSERVED_CALLS_SHOWN);

  return (
    <div className="card glass grow">
      <div className="panel-h">
        <span className="label">Following</span>
        <span className={`chip ${stateChip(ship.state)}`}>{stateLabel(ship.state)}</span>
      </div>
      <div className="body scroll">
        <div style={{ fontSize: 16, fontWeight: 700 }}>{ship.name}</div>
        <div className="mono dim" style={{ fontSize: 10.5, margin: "2px 0 10px" }}>
          {ship.line.toUpperCase()} · {ship.imo.startsWith("ENI") ? `ENI ${ship.imo.slice(3)}` : `IMO ${ship.imo}`}
        </div>

        <div className="kv">
          <span className="k">LAST AIS POSITION</span>
          <span className="v">{minutesAgo(ship.minutesSinceLastPosition)}</span>
        </div>
        {pos && (
          <>
            <div className="kv">
              <span className="k">POSITION</span>
              <span className="v">{latLon(pos.latitude, pos.longitude)}</span>
            </div>
            <div className="kv">
              <span className="k">SPEED · COURSE</span>
              <span className="v">
                {knots(pos.sog)} · {degrees(pos.cog)}
              </span>
            </div>
          </>
        )}
        <div className="kv">
          <span className="k">NEAR PORT</span>
          <span className="v">{ship.nearPort ? `${ship.nearPort.name} (${ship.nearPort.distanceNm} nm)` : "—"}</span>
        </div>
        <div className="kv">
          <span className="k">DESTINATION (AIS)</span>
          <span className="v">
            {ship.reportedDestination ?? "—"}
            {ship.reportedDestinationPort && (
              <>
                <span className="faint"> · </span>
                {ship.reportedDestinationPort.name}
              </>
            )}
          </span>
        </div>
        <div className="kv">
          <span className="k">PASSENGER CAPACITY</span>
          <span className="v">{ship.passengerCapacity ? thousands(ship.passengerCapacity) : "—"}</span>
        </div>
        {ship.state === "no-recent-signal" && (
          <p className="note" style={{ marginTop: 8 }}>
            No AIS message for over an hour — the ship is probably out of range of coastal receivers. The map shows
            where it was last heard.
          </p>
        )}

        <div className="label" style={{ margin: "14px 0 6px" }}>
          Port calls seen in AIS · UTC
        </div>
        {observedCalls.length === 0 ? (
          <p className="note">No stops seen in the last 30 days.</p>
        ) : (
          <div className="tl">
            {observedCalls.map((call) => {
              const here = call.departedBefore === null;
              return (
                <div key={call.arrivedAt} className={`tl-item ${here && ship.state !== "no-recent-signal" ? "now" : "done"}`}>
                  <div className="port">
                    {call.portName ?? "Stopped away from a known port"}
                    {call.mooring === "anchor" && <span className="faint"> · at anchor</span>}
                  </div>
                  <div className="times">
                    {observedArrival(call)} · {observedDeparture(call, ship.state !== "no-recent-signal")}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="label" style={{ margin: "14px 0 6px" }}>
          Calls at tracked ports
        </div>
        {calls.length === 0 ? (
          <p className="note">No calls at tracked ports in the next 30 days.</p>
        ) : (
          <div className="tl">
            {calls.map((call) => {
              const tz = timezones.get(call.portLocode) ?? "UTC";
              return (
                <div key={`${call.portLocode}-${callStart(call)}`} className={`tl-item ${callPhase(call, now)}`}>
                  <div className="port">{call.portName}</div>
                  <div className="times">
                    {portDate(callStart(call), tz)} · {callTimes(call, tz)}
                    {call.berth ? ` · berth ${call.berth}` : ""}
                  </div>
                  {(call.actualArrival || call.actualDeparture) && (
                    <div className="times" style={{ color: "var(--ok)" }}>
                      {actualTimes(call, tz)} (reported by port)
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        <p className="note" style={{ marginTop: 10 }}>
          Times are local to each port. {SCHEDULE_NOTE}
        </p>
      </div>
    </div>
  );
}

/** What our AIS feed can see at this port, from its own schedule (see aisCoverageSchema). */
function AisCoverageNote({ coverage }: { coverage: AisCoverage }) {
  const since = new Intl.DateTimeFormat("en-GB", { timeZone: "UTC", day: "numeric", month: "short" }).format(
    new Date(coverage.since),
  );
  const { fleetCalls, fleetCallsHeard } = coverage;
  switch (coverage.status) {
    case "none":
      return (
        <p className="note" style={{ color: "var(--warn)" }}>
          No AIS coverage at this port: none of the {fleetCalls} fleet call{fleetCalls === 1 ? "" : "s"} here since{" "}
          {since} were heard within 30 km. Positions, destinations and AIS arrival times are missing here.
        </p>
      );
    case "partial":
      return (
        <p className="note">
          Patchy AIS coverage: {fleetCallsHeard} of {fleetCalls} fleet calls here since {since} were heard within 30 km.
        </p>
      );
    case "good":
      return (
        <p className="note">
          AIS heard all {fleetCalls} fleet call{fleetCalls === 1 ? "" : "s"} here since {since}.
        </p>
      );
    case "unknown":
      return <p className="note">AIS coverage not measured yet: no fleet ship was scheduled here since {since}.</p>;
  }
}

export function PortDayCard({
  port,
  onSelectShip,
}: {
  port: PortSummary | null;
  onSelectShip: (imo: string) => void;
}) {
  if (!port) return null;
  const { today } = port;
  return (
    <div className="card glass">
      <div className="panel-h">
        <span className="label">Port day · {port.name}</span>
        {port.hasSchedule ? (
          <span className={`chip ${today.shipsScheduled >= 4 ? "warn" : "dim"}`}>{today.shipsScheduled} DUE</span>
        ) : (
          <span className="chip dim">NO SCHEDULE</span>
        )}
      </div>
      {port.hasSchedule ? (
        <>
          <div className="statgrid">
            <div className="stat">
              <div className="label">Ships today</div>
              <div className="v">{today.shipsScheduled}</div>
            </div>
            <div className="stat">
              <div className="label">Known capacity</div>
              <div className="v">
                {compact(today.knownPassengerCapacity)}
                <small> pax</small>
              </div>
            </div>
          </div>
          <div className="body scroll" style={{ maxHeight: 220 }}>
            <div style={{ marginBottom: 8 }}>
              <AisCoverageNote coverage={port.aisCoverage} />
            </div>
            {today.calls.length === 0 && <p className="note">No ships scheduled today.</p>}
            {today.calls.map((call) => (
              <div key={`${call.shipName}-${callStart(call)}`} className="list-row">
                {call.shipImo ? (
                  <button onClick={() => onSelectShip(call.shipImo!)}>{call.shipName}</button>
                ) : (
                  <span className="dim">{call.shipName}</span>
                )}
                <span className="mono dim" style={{ fontSize: 10, textAlign: "right" }}>
                  {callTimes(call, port.timezone)}
                  {(call.actualArrival || call.actualDeparture) && (
                    <span style={{ display: "block", color: "var(--ok)" }} title="Reported by the port authority">
                      {actualTimes(call, port.timezone)}
                    </span>
                  )}
                </span>
              </div>
            ))}
            {today.shipsWithoutCapacity > 0 && (
              <p className="note" style={{ marginTop: 8 }}>
                Capacity counts only ships in our registry: {today.shipsWithoutCapacity} of {today.shipsScheduled} are
                not in it yet.
              </p>
            )}
          </div>
        </>
      ) : (
        <div className="body note">
          This port publishes its schedule in a form we cannot read yet (scanned PDF or a page that blocks automated
          access).
        </div>
      )}
    </div>
  );
}

export function NearbyShipsCard({
  port,
  onSelectShip,
}: {
  port: PortSummary | null;
  onSelectShip: (imo: string) => void;
}) {
  if (!port) return null;
  return (
    <div className="card glass">
      <div className="panel-h">
        <span className="label">Fleet ships within 25 nm</span>
      </div>
      <div className="body">
        {port.fleetShipsNearby.length === 0 ? (
          <p className="note">No tracked fleet ship is near {port.name} right now.</p>
        ) : (
          port.fleetShipsNearby.map((s) => (
            <div key={s.imo} className="list-row">
              <button onClick={() => onSelectShip(s.imo)}>{s.name}</button>
              <span className="mono dim" style={{ fontSize: 10 }}>
                {s.distanceNm} nm · {stateLabel(s.state)}
              </span>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export function PortListCard({
  ports,
  selected,
  onSelect,
}: {
  ports: PortSummary[];
  selected: string | null;
  onSelect: (locode: string) => void;
}) {
  // Busiest first, then by name: with dozens of ports the ones with ships due matter most.
  const sorted = [...ports].sort(
    (a, b) =>
      Number(b.hasSchedule) - Number(a.hasSchedule) ||
      b.today.shipsScheduled - a.today.shipsScheduled ||
      a.name.localeCompare(b.name),
  );
  return (
    <div className="card glass grow">
      <div className="panel-h">
        <span className="label">Tracked ports · today</span>
        <span className="mono dim" style={{ fontSize: 10 }}>
          {ports.length}
        </span>
      </div>
      <div className="body scroll">
        {sorted.map((p) => (
          <div key={p.locode} className="list-row">
            <button
              onClick={() => onSelect(p.locode)}
              style={p.locode === selected ? { color: "var(--accent)" } : undefined}
            >
              {p.name}
            </button>
            <span className="mono dim" style={{ fontSize: 10 }}>
              {p.hasSchedule ? `${p.today.shipsScheduled} due` : "no schedule"}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function CallsStrip({
  ship,
  detail,
  timezones,
  now,
}: {
  ship: ShipNow | null;
  detail: ShipDetailResponse | null;
  timezones: Timezones;
  now: number;
}) {
  const calls = ship && detail?.ship.imo === ship.imo ? detail.calls.slice(0, 7) : [];
  if (!ship) return null;
  if (calls.length === 0) {
    return (
      <div id="bottombar" className="glass">
        <div className="seg">
          <div className="k">Upcoming calls · {ship.name}</div>
          <div className="v dim" style={{ fontSize: 11, fontWeight: 400 }}>
            none at tracked ports in the next 30 days
          </div>
        </div>
      </div>
    );
  }
  return (
    <div id="bottombar" className="glass">
      {calls.map((call) => {
        const tz = timezones.get(call.portLocode) ?? "UTC";
        const phase = callPhase(call, now);
        return (
          <div key={`${call.portLocode}-${callStart(call)}`} className={`daypill ${phase === "now" ? "today" : ""}`}>
            <span className="d">{portDate(callStart(call), tz).toUpperCase()}</span>
            <span className="p">{call.portName}</span>
            <span className="st" style={phase === "done" ? { color: "var(--ok)" } : undefined}>
              {phase === "now" ? "● IN PORT (SCHEDULED)" : phase === "done" ? "✓ DEPARTED (SCHEDULED)" : callTimes(call, tz)}
            </span>
          </div>
        );
      })}
    </div>
  );
}
